import { Fragment } from 'react';
import { Box, Center, Text } from '@chakra-ui/react';
import MessageBubble from './MessageBubble';
import CommBookCard from './CommBookCard';
import { formatDate } from '../../utils/dateUtils';

// 時間軸：混排 text/image/comm_book。comm_book 用全寬卡片、其餘用左右氣泡；日期變更插入分隔線。
// onSign（optional）：只有家長對話室會傳入，轉發給 CommBookCard 顯示「我要簽收」鈕；
// 老師端不傳，CommBookCard 就只顯示簽收狀態、不顯示按鈕（老師不能代簽）。
// pending（optional）：useOptimisticSend 產生的樂觀訊息，永遠接在最後（它們就是剛送出的最新一則）。
// 刻意「渲染時才合併」而不是併進上層的 messages 狀態——上層對 messages 有 id 數字排序與去重邏輯，
// 混入 `tmp-x` 字串 id 會壞掉。onRetry 轉發給氣泡上的紅色驚嘆號。
// onRecall（optional）：只有需要「收回訊息」入口的頁面會傳（老師 PWA 對話室、桌面對話頁）；
// 不傳的頁面（家長 App）氣泡上不會出現任何收回入口，渲染與加這個參數之前逐字相同。
// orgView（optional，預設 false）：機構端（老師 PWA 對話室、桌面對話頁）傳 true，改用
// 「家長靠左／機構靠右」呈現，理由見 MessageBubble 的 alignRight。這裡只是原樣轉發，
// 不傳的呼叫端（家長 App）連帶氣泡都走原本的分支。
export default function MessageList({ messages, currentUserId, onImageClick, onSign, pending, onRetry, onRecall, onContextMenu, orgView = false }) {
    const all = pending && pending.length ? [...(messages || []), ...pending] : (messages || []);

    if (all.length === 0) {
        return (
            <Center py={16}>
                <Text fontSize="sm" color="onCanvas.300">還沒有訊息，開始對話吧</Text>
            </Center>
        );
    }

    // 日期分隔線與「這一則要不要標發送者姓名」都只跟**前一則**有關，所以在 map 裡直接回頭看
    // all[i - 1]，而不是在 render 過程中改寫一個外部變數。兩者算出來的結果完全相同
    //（第一則的 prev 是 undefined，等同原本 lastDay 的初始值 null），差別在後者是
    // render 期間的副作用，react-hooks 的 immutability 規則會擋（本檔原本就有一個那樣的 error）。
    const rows = all.map((m, i) => {
        const prev = all[i - 1];
        const day = formatDate(m.created_at);
        const showDivider = day !== (prev ? formatDate(prev.created_at) : null);
        return {
            m,
            day,
            showDivider,
            // 發送者姓名只在「這一串的第一則」顯示（LINE/Slack 的慣例）。一位學生可以綁多位家長，
            // 老師必須分得出是爸爸還是媽媽；但同一個人連發三則就標三次名字，重複的字反而會被眼睛
            // 直接略過——標在**換人的那一則**上，這行字才是資訊而不是背景噪音。
            // 一串會被三件事打斷：換日期（分隔線本來就把版面切開了）、中間插進整片的聯絡簿卡片、
            // 以及對方換人（含自己發的訊息——自己的氣泡靠右且不標名字，天然就是一個斷點）。
            //
            // 【聯絡簿卡片為什麼不標發送者】comm_book 走 CommBookCard、不走氣泡，而它的語意是
            // 「發給全班每位家長的聯絡簿」不是某個人對你說的話（排程發送時後端甚至沒有發送者）。
            // 掛一個人名上去會讓家長把它讀成老師單獨傳給自己的訊息。卡片自帶「電子聯絡簿」標頭
            // 與日期，來源本來就講清楚了。
            startsRun: showDivider
                || !prev
                || prev.content_type === 'comm_book'
                || prev.sender_id !== m.sender_id,
        };
    });

    return (
        <Box>
            {rows.map(({ m, day, showDivider, startsRun }) => (
                <Fragment key={m.id}>
                    {showDivider && (
                        <Center my={3}>
                            <Text fontSize="11px" color="neutral.400" bg="neutral.100" px={3} py={0.5} borderRadius="full">
                                {day}
                            </Text>
                        </Center>
                    )}
                    {m.content_type === 'comm_book' ? (
                        <CommBookCard message={m} onImageClick={onImageClick} onSign={onSign} />
                    ) : (
                        <MessageBubble
                            message={m}
                            isMine={m.sender_id === currentUserId}
                            showSenderName={startsRun}
                            orgView={orgView}
                            onImageClick={onImageClick}
                            onRetry={onRetry}
                            onRecall={onRecall}
                            onContextMenu={onContextMenu}
                        />
                    )}
                </Fragment>
            ))}
        </Box>
    );
}
